import { useState } from 'react'

import {
  BloodType,
  DriverLicense,
  Gender,
  MaritalStatus,
  Maybe,
  Religion,
} from '@/_gql/graphql'

import {
  BLOOD_TYPE_OPTIONS,
  DRIVER_LICENSE_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RELIGION_OPTIONS,
} from '@/services/user/constant'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

import {
  checkBooleanValue,
  checkEverValue,
  checkHaveValue,
} from '@/lib/utils/utils'

import { dateFormat } from '@/lib/formatter'

interface PersonalInformationProps {
  name?: string
  handphone_number?: string | null | undefined
  email?: string
  birthdate?: Maybe<string>
  address?: string
  city?: {
    id: string
    name: string
  }
  province?: {
    id?: string
    name?: string
  }
  gender?: Maybe<Gender>
  is_single_child: boolean | undefined
  marital_status?: Maybe<MaritalStatus>
  religion?: Maybe<Religion>
  driver_license?: DriverLicense | null
  blood_type?: BloodType | null
  weight?: number
  height?: number
  is_ever_been_going_abroad?: boolean
  is_ever_proposing_passport?: boolean
  is_having_passport?: boolean
  photo?: string
  nationality?: {
    id?: string
    name?: string
  }
  country?: {
    id?: string
    name?: string
  }
}
export function PersonalInformation(props: PersonalInformationProps) {
  const [show, setShow] = useState<boolean>(true)
  const name = props?.name || ''
  const handphone_number = props.handphone_number

  const {
    email,
    birthdate,
    address,
    is_single_child,
    blood_type,
    city,
    driver_license,
    gender,
    height,
    is_ever_been_going_abroad,
    is_ever_proposing_passport,
    is_having_passport,
    marital_status,
    province,
    weight,
    religion,
    photo,
    country,
    nationality,
  } = props

  const province_birth_place = province && province.name ? province.name : ''
  const birth_place =
    city && city.name
      ? `${city.name}, ${province_birth_place}, ${country?.name}`
      : '-'
  const gender_value = GENDER_OPTIONS.find(
    (t) => t.value == gender?.toUpperCase()
  )
  const marital_status_value = marital_status
    ? MARITAL_STATUS_OPTIONS.find(
        (t) => t.value == marital_status.toUpperCase()
      )?.label
    : '-'
  const driver_licence_value = driver_license
    ? DRIVER_LICENSE_OPTIONS.find(
        (t) => t.value == driver_license.toUpperCase()
      )?.label
    : 'Belum'
  const religion_value = religion
    ? RELIGION_OPTIONS.find((t) => t.value == religion.toUpperCase())?.label
    : '-'
  const blood_type_value = blood_type
    ? BLOOD_TYPE_OPTIONS.find((t) => t.value == blood_type.toUpperCase())?.label
    : '-'

  function getProfileImage(gender?: Gender, url?: string) {
    if (url && url.length > 0) {
      return url
    } else {
      if (gender?.toUpperCase() == Gender.Men) {
        return '/img/default-user-man.jpg'
      } else {
        return '/img/default-user-woman.jpg'
      }
    }
  }
  return (
    <>
      <Accordion
        defaultValue="personal-information"
        type="single"
        collapsible
        className="w-full rounded-md border-t-4 border-t-[#D91F2E] bg-white"
        onValueChange={(value) => setShow(value === 'personal-information')}
      >
        <AccordionItem value="personal-information" className="border-none p-0">
          <AccordionTrigger className="border-b border-b-[#EAECF0] p-6">
            <div className="flex w-full items-center justify-between">
              <div>Informasi Pribadi</div>
              <div className="text-xs">{show ? 'Sembunyikan' : ''}</div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="w-full p-6">
            <div className="flex w-full gap-3">
              <div className="w-1/6">
                <img
                  className="h-36 w-36"
                  src={getProfileImage(gender || Gender.Men, photo)}
                />
              </div>
              <div className="grid w-5/6 grid-cols-4 gap-4 gap-y-6">
                <div className="flex flex-col">
                  <div className="font-semibold">Nama</div>
                  <div>{name}</div>
                </div>
                <div className="flex flex-col">
                  <div className="font-semibold">Nomor Hp</div>
                  <div>{handphone_number}</div>
                </div>
                <div className="word-wrap white-space flex flex-col">
                  <div className="font-semibold">Email</div>
                  <div className="overflow-hidden break-words">
                    {email || '-'}{' '}
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="font-semibold">Tanggal Lahir</div>
                  <div>{birthdate ? dateFormat(birthdate) || '-' : '-'}</div>
                </div>
                <div className="col-span-2 flex flex-col">
                  <div className="font-semibold">Alamat</div>
                  <div className="overflow-hidden break-words">
                    {address || ''}
                  </div>
                </div>
                <div className="col-span-2 flex hidden flex-col">
                  {' '}
                  {/* hide */}
                  <div className="overflow-hidden break-words font-semibold">
                    Tempat lahir
                  </div>
                  <div>{birth_place}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Jenis Kelamin</div>
                  <div>{gender_value?.label}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Anak Tunggal</div>
                  <div>{checkBooleanValue(is_single_child)}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Status Pernikahan</div>
                  <div>{marital_status_value}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Agama</div>
                  <div>{religion_value}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Kewarganegaraan</div>
                  <div>{nationality?.name}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">SIM Mobil</div>
                  <div>{driver_licence_value}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Golongan Darah</div>
                  <div>{blood_type_value}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Berat Badan</div>
                  <div>{weight}kg</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Tinggi Badan</div>
                  <div>{height}cm</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Pernah Keluar Negeri</div>
                  <div>{checkBooleanValue(is_ever_been_going_abroad)}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Paspor</div>
                  <div>{checkHaveValue(is_having_passport)}</div>
                </div>
                <div className="flex hidden flex-col">
                  <div className="font-semibold">Ajukan Paspor</div>
                  <div>{checkEverValue(is_ever_proposing_passport)}</div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  )
}
